import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IOrderRow } from '../../../Entities';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';
import { useDoneEditingMutation } from '../queries/use-mutation-done-editing';
import { canMarkDoneEditing } from '../state/utils';

const SectionContainer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
        width: '50%'
    }
});

const SectionHeader = styled('header')({
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '16px'
});

const SectionForm = styled('form')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderTop: 'none',
    padding: '16px'
});

type DoneEditingSectionProps = {
    orderId: number;
    user: AuthenticatedUser;
    photoStatus: ImageEditorPhotoStatus;
    editedOrderRows: IOrderRow[];
};

export function DoneEditingSection({
    orderId,
    user,
    photoStatus,
    editedOrderRows
}: DoneEditingSectionProps) {
    let doneEditing = useDoneEditingMutation({ orderId, user });

    if (!canMarkDoneEditing({ user, photoStatus, editedOrderRows })) {
        return null;
    }

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        doneEditing.mutate();
    }

    return (
        <SectionContainer>
            <Box component="section">
                <SectionHeader>
                    <Typography variant="h2" sx={{ fontSize: '16px' }}>
                        {Translate('image-processing/page/status-actions/header/done-editing-images')}
                    </Typography>
                </SectionHeader>
                <SectionForm onSubmit={handleSubmit}>
                    <Typography variant="body1" sx={{ fontSize: '14px' }}>
                        {Translate(
                            'image-processing/page/status-actions/description/done-editing-images'
                        )}
                    </Typography>
                    <Button
                        type="submit"
                        variant="contained"
                        data-testid="done-editing-images"
                        disabled={doneEditing.isLoading}
                        aria-busy={doneEditing.isLoading}
                        startIcon={doneEditing.isLoading ? <CircularProgress size={20} /> : undefined}
                    >
                        {Translate('image-processing/page/status-actions/button-label/done')}
                    </Button>
                </SectionForm>
            </Box>
        </SectionContainer>
    );
}
